import axios from 'axios'
import {
  isLocalDevWithBackend,
  isProduction,
  isStg,
} from '../app-config/environment'
import { STG_ENV } from '../app-config/stg-env'

const ENROLLMENT_PLATFORM_API_URL = {
  DEV: 'https://api.dev.surecolabs.com',
  PROD: 'https://services.sureco.com',
  LOCAL: 'http://localhost:3010',
  STG: STG_ENV.apiUrl,
}

export const getApiUrl = () => {
  if (isStg()) {
    return ENROLLMENT_PLATFORM_API_URL.STG
  }
  if (isProduction()) {
    return ENROLLMENT_PLATFORM_API_URL.PROD
  }
  if (isLocalDevWithBackend()) {
    return ENROLLMENT_PLATFORM_API_URL.LOCAL
  }
  return ENROLLMENT_PLATFORM_API_URL.DEV
}

export const getLocalApiUrl = () => {
  return ENROLLMENT_PLATFORM_API_URL.LOCAL
}

/**
 * Sends ICHRA email for batch of member IDs
 */
export const sendIchraBatch = async (
  memberIds: string[]
): Promise<{ [key: string]: boolean | string }> => {
  const response = await axios.post(
    '/api/v1/messaging/members/send-ichra',
    { memberIds },
    { baseURL: getApiUrl() }
  )

  return response.data
}

/**
 * Queues the original welcome email for batch of member IDs.
 * Each value is `true` when queued, or an error message when skipped.
 */
export const sendWelcomeBatch = async (
  memberIds: string[]
): Promise<{ [key: string]: boolean | string }> => {
  const response = await axios.post(
    '/api/v1/messaging/members/send-welcome',
    { memberIds },
    { baseURL: getApiUrl() }
  )

  return response.data
}

export type WaiveBenefitsEmployee = {
  employeePublicKey: string
  reason: string
  contactChannel: string
}

export type WaiveBenefitsRequest = {
  clientPublicKey: string
  year: number
  // When true, the API validates the waivers without applying them
  dryRun: boolean
  employees: WaiveBenefitsEmployee[]
}

export type WaiveBenefitsStatus = 'success' | 'noEffect' | 'unsuccessful'

export type WaiveBenefitsResponse = {
  employees: {
    employeePublicKey: string
    status: WaiveBenefitsStatus
    reason?: string
  }[]
}

/**
 * Waives all benefits for a batch of employees
 */
export const waiveBenefits = async (
  request: WaiveBenefitsRequest
): Promise<WaiveBenefitsResponse> => {
  const response = await axios.post(
    '/api/v1/enrollments/waive-benefits',
    request,
    { baseURL: getApiUrl() }
  )

  return response.data
}

type ValidationErrorNode = {
  field: string
  errors: (string | ValidationErrorNode)[]
}

const collectMessages = (errors: (string | ValidationErrorNode)[]): string[] =>
  errors.flatMap((error) =>
    typeof error === 'string' ? [error] : collectMessages(error.errors ?? [])
  )

/**
 * Reads a 422 waive-benefits response into error messages per employee,
 * keyed by the employee's index in the request payload.
 * Returns null when the error isn't an employee validation error.
 */
export const getWaiveBenefitsEmployeeErrors = (
  error: unknown
): Record<number, string[]> | null => {
  const response = (error as { response?: { status?: number; data?: any } })
    ?.response
  if (response?.status !== 422 || !Array.isArray(response.data?.message)) {
    return null
  }

  const employeesNode = (response.data.message as ValidationErrorNode[]).find(
    (node) => node?.field === 'employees'
  )
  if (!employeesNode) return null

  const errorsByIndex: Record<number, string[]> = {}
  employeesNode.errors.forEach((node) => {
    if (typeof node === 'string') return
    const index = Number(node.field)
    if (Number.isInteger(index)) {
      errorsByIndex[index] = collectMessages(node.errors ?? [])
    }
  })

  return errorsByIndex
}
